import { z } from 'zod';

import { LEGACY_IDENTITY_ID_PATTERN } from './identity-id';

// The grammar is the member-id shape, not RFC 4122 v4. Under zod 4 `.uuid()`
// checks the version and variant nibbles, which a migrated member's imported
// backend2 id does not carry — validating the subject as v4 dropped every such
// member out of RUM, PostHog and experiments. Admission is by shape; the
// narrowing that matters here is "an opaque member id and nothing else", and
// an email, a phone number, a display name or the retired 64-char tokenized
// subject is still refused.
export const analyticsSubjectIdSchema = z
  .string()
  .regex(LEGACY_IDENTITY_ID_PATTERN, 'Expected a canonical actor id')
  .brand<'AnalyticsSubjectId'>();

/**
 * The Datadog `usr.id` boundary guard (ADR-069): returns the id only when it is
 * the raw canonical member id, else `undefined`. Every telemetry emission seam
 * (context setter, direct log fields, service-event correlation) routes through
 * this so a misconfigured issuer or a non-id claim can never leak a
 * non-canonical identifier into telemetry.
 */
export function canonicalUsrId(id: string | undefined): string | undefined {
  return id && analyticsSubjectIdSchema.safeParse(id).success ? id : undefined;
}
export const analyticsIdentityIdSchema = analyticsSubjectIdSchema;

export const analyticsActorTypeSchema = z.enum(['member', 'staff']);
// Preview environments are keyed by the lowercased ticket id (`gal-1349`), with
// `pr-{N}` only as the fallback for a ticketless branch. Matching `pr-\d+` alone
// rejected every ticket-keyed preview, so `environmentFromEventSource` returned
// undefined and the forwarder fell back to personless — silently dropping the
// AnalyticsSubject for the environments most analytics work is verified in. This
// pattern is the key shape `resolve-preview-key.sh` emits, and still admits the
// `pr-{N}` fallback.
const PREVIEW_ENV_KEY = /^[a-z][a-z0-9]*-\d+$/;

export const analyticsEnvironmentSchema = z
  .enum(['local', 'test', 'dev', 'prod'])
  .or(z.string().regex(PREVIEW_ENV_KEY));

export const analyticsSubjectInputSchema = z
  .object({
    actorId: z
      .string()
      .min(1)
      .max(200)
      .refine(
        (value) =>
          [...value].every((character) => {
            const codePoint = character.codePointAt(0) ?? 0;
            return codePoint >= 32 && codePoint !== 127;
          }),
        'Actor id must not contain controls',
      ),
    actorType: analyticsActorTypeSchema,
    environment: analyticsEnvironmentSchema,
  })
  .strict();

export type AnalyticsSubjectId = z.infer<typeof analyticsSubjectIdSchema>;
export type AnalyticsIdentityId = AnalyticsSubjectId;
export type AnalyticsActorType = z.infer<typeof analyticsActorTypeSchema>;
export type AnalyticsEnvironment = z.infer<typeof analyticsEnvironmentSchema>;
export type AnalyticsSubjectInput = z.infer<typeof analyticsSubjectInputSchema>;

export function normalizeAnalyticsEnvironment(value: string | undefined): AnalyticsEnvironment {
  const normalized = value?.trim().toLowerCase();
  if (normalized === 'prod' || normalized === 'production') return 'prod';
  if (normalized === 'test') return 'test';
  if (normalized === 'dev' || normalized === 'development' || normalized === 'preview') {
    return 'dev';
  }
  const parsed = analyticsEnvironmentSchema.safeParse(normalized);
  if (parsed.success) return parsed.data;
  return 'local';
}
